export const API_URL = import.meta.env.VITE_API_URL ?? "/api";

const API_REQUEST_TIMEOUT_MS = 15_000;
const SYNC_SUBMIT_TIMEOUT_MS = 45_000;

export type UserRole = "employee" | "subcontractor" | "admin" | "manager" | "readonly_admin";

export type User = {
  id: number;
  email: string;
  full_name: string;
  company_name: string;
  account_id: string;
  qb_username: string | null;
  qb_user_email?: string | null;
  qb_contact_id?: string | null;
  qb_vendor_id?: string | null;
  qb_payroll_code: string | null;
  qb_payroll_id?: string | null;
  co_code?: string | null;
  office_location?: string | null;
  role: UserRole;
  is_active: boolean;
  dark_mode: boolean;
  recent_compact: boolean;
  mfa_enabled: boolean;
  is_manager?: boolean;
  /** Entra readonly-admin group membership (dual with manager → account-wide read). */
  is_readonly_admin_group?: boolean;
  manager_qb_user_email?: string | null;
  team_qb_record_id?: string | null;
  team_name?: string | null;
  /** Server flag ENABLE_MANAGER_PUNCH_CREATION (from GET /auth/me). */
  enable_manager_punch_creation?: boolean;
  invite_send_on?: string | null;
  invite_sent_at?: string | null;

};

export type OnBehalfPunchPayload = {
  employee_user_id: number;
  clock_in_time: string;
  clock_out_time?: string | null;
  tz_name?: string | null;
  tz_offset_minutes?: number | null;
  /** Required: same as self-service clock-in. */
  cost_code_id: number;
  /** Required: site / customer. */
  customer_id: number;
  site_name?: string | null;
  notes?: string | null;
  task?: string | null;
};

export type Job = {
  id: number;
  name: string;
  active: boolean;
};

export type CostCode = {
  id: number;
  qb_record_id: string;
  cost_code: string;
  general_ledger: string | null;
  class_id: string | null;
  active: boolean;
};

export type Customer = {
  id: number;
  qb_record_id: string;
  customer_id: string;
  company: string;
};

export type Entry = {
  id: number;
  user_id: number;
  job_id: number | null;
  cost_code_id: number | null;
  customer_id: number | null;
  action: "clock_in" | "clock_out";
  user_role: UserRole;
  account_id: string;
  clock_status: "clocked_in" | "clocked_out";
  qb_record_id: string | null;
  /** Deep link to QuickBase punch record when synced. */
  qb_record_url?: string | null;
  technician_name: string;
  company_name: string;
  site_name: string;
  co_code?: string | null;
  synergy_employee_name: string;
  recorded_time: string;
  submitted_at: string;
  user_email: string;
  employee_name: string;
  job_name: string | null;
  cost_code_name: string | null;
  customer_name: string | null;
  /** Customer.customer_id business string (e.g. "ca-site-1") for CA OT. */
  customer_code?: string | null;
  /** True when clock_out was before clock_in. */
  invalid_punch?: boolean;
  selfie_path: string | null;
  /** Clock-in Task form field (separate from Notes). */
  task: string | null;
  notes: string | null;
  /** Clock-out free-text notes (from punch / QB). */
  clock_out_notes?: string | null;
  /** Clock-out Task form field (separate from clock-out notes). */
  clock_out_task?: string | null;
  /** Meals & breaks Yes/No from clock-out form. */
  meals_breaks?: string | null;
  /** Injury Yes/No from clock-out form. */
  injured?: string | null;
  /** Meals & breaks notes from clock-out form. */
  break_notes?: string | null;
  /** Injury description from clock-out form. */
  injury_notes?: string | null;
  field_notes: string | null;
  task_needs_review?: boolean;
  notes_needs_review?: boolean;
  clock_out_task_needs_review?: boolean;
  clock_out_notes_needs_review?: boolean;
  injury_notes_needs_review?: boolean;
  break_notes_needs_review?: boolean;
  field_notes_needs_review?: boolean;
  gps_lat: string | null;
  gps_lon: string | null;
  gps_accuracy: string | null;
  clock_out_time: string | null;
  clock_out_gps_lat: string | null;
  clock_out_gps_lon: string | null;
  clock_out_selfie_path: string | null;
  duration: number | null;
  // The timezone the punch timestamp was recorded in (from client at submit time).
  // Used for display so user sees the exact local value they recorded.
  tz_name?: string;
  tz_offset_minutes?: number | null;

  // Clock-out action capture tz + raw submission values (symmetric to the
  // clock-in tz_name / current_time_*). Populated on clock-out submits.
  // For display we show standardized ET primary + these as the "original
  // recorded" provenance.
  clock_out_tz_name?: string;
  clock_out_tz_offset_minutes?: number | null;
  clock_out_current_time_raw?: string | null;
  clock_out_current_time_iso?: string | null;

  // Geofence outside checkbox + distance (miles) pulled from QuickBase formulas on sync.
  // Present on admin report entries after timeclock sync.
  clock_in_outside_geofence?: boolean | null;
  clock_in_outside_geofence_distance?: number | null;
  clock_out_outside_geofence?: boolean | null;
  clock_out_outside_geofence_distance?: number | null;

  // Admin/manager punch corrections — true when that field was later changed.
  corrected_clock_in?: boolean;
  corrected_clock_out?: boolean;
  corrected_site?: boolean;
  corrected_cost_code?: boolean;
};

export type FieldNote = {
  id: number;
  user_id: number;
  additional_note: string;
  correction_type: string | null;
  original_datetime: string | null;
  corrected_datetime: string | null;
  reason_for_correction: string | null;
  submitted_at: string;
  qb_record_id: string | null;
  /** Deep link to QuickBase record when synced (server-built). */
  qb_record_url?: string | null;
  processed: boolean;
  processed_at: string | null;
  processed_by_user_id: number | null;
  acknowledged: boolean;
  acknowledged_by: string | null;
  account_id: string;
  user_email?: string;
  employee_name?: string;
};

export type PunchNoteFlaggedField = {
  field: string;
  flag: string;
  label: string;
  text: string;
};

export type PunchNoteReviewRead = {
  entry_id: number;
  user_id: number;
  employee_name?: string | null;
  user_email?: string | null;
  recorded_time: string;
  qb_record_url?: string | null;
  meals_breaks?: string | null;
  injured?: string | null;
  flagged_fields: PunchNoteFlaggedField[];
};

export type VendorSignupResponse = {
  id: number;
  vendor_name: string;
  vendor_email: string;
  vendor_phone: string;
  notify_jobs: string;
  job_industry: string;
  desired_pay_rate: string;
  service_radius: string;
  submitted_at: string;
};

export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown) {
    super(formatApiDetail(detail));
    this.status = status;
    this.detail = detail;
  }
}

class RequestTimeoutError extends Error {
  constructor(message = "Network request timed out.") {
    super(message);
    this.name = "RequestTimeoutError";
  }
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = API_REQUEST_TIMEOUT_MS): Promise<Response> {
  if (typeof fetch !== "function") {
    throw new TypeError("Network requests are unavailable.");
  }

  if (typeof AbortController !== "undefined") {
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } catch (error) {
      if ((error as { name?: string } | null)?.name === "AbortError") {
        throw new RequestTimeoutError();
      }
      throw error;
    } finally {
      globalThis.clearTimeout(timeout);
    }
  }

  let timeout: ReturnType<typeof globalThis.setTimeout> | null = null;
  try {
    return await Promise.race([
      fetch(input, init),
      new Promise<Response>((_, reject) => {
        timeout = globalThis.setTimeout(() => reject(new RequestTimeoutError()), timeoutMs);
      }),
    ]);
  } finally {
    if (timeout !== null) globalThis.clearTimeout(timeout);
  }
}

function formatApiDetail(detail: unknown): string {
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) return String(item.msg);
        return JSON.stringify(item);
      })
      .join(" ");
  }
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "detail" in detail) return formatApiDetail((detail as { detail: unknown }).detail);
  return "Request failed.";
}

export async function refreshToken() {
  // Hits /auth/refresh which (on success) rotates and sets new httpOnly cookies for access+refresh.
  // Returns tokens for native clients that manage them in SecureStore; web relies on cookies.
  return request<{ access_token?: string; refresh_token?: string; token_type?: string }>("/auth/refresh", {
    method: "POST",
  });
}

let _refreshInFlight: Promise<boolean> | null = null;
/** After a hard refresh failure, skip further refresh attempts (and optional fail-fast) briefly. */
let _authDeadUntil = 0;
const AUTH_DEAD_COOLDOWN_MS = 30_000;

/** Clear the auth circuit breaker (call after successful login). */
export function clearAuthCircuit(): void {
  _authDeadUntil = 0;
}

function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/auth/");
}

async function tryRefresh(): Promise<boolean> {
  if (Date.now() < _authDeadUntil) return false;
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const response = await fetchWithTimeout(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        _authDeadUntil = 0;
        return true;
      }
      // 401/403 means no refresh cookie — cool down so parallel queries don't thrash radio.
      if (response.status === 401 || response.status === 403) {
        _authDeadUntil = Date.now() + AUTH_DEAD_COOLDOWN_MS;
      }
      return false;
    } catch {
      // Network blip: do not mark auth dead; caller may retry later.
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) headers.set("Content-Type", "application/json");

  // After a hard auth failure, skip non-auth traffic for a short window so dashboard
  // fan-out (entries + cost-codes + customers + …) does not hammer cellular.
  // Auth endpoints still go through so login/refresh can recover.
  if (!isAuthEndpoint(path) && Date.now() < _authDeadUntil) {
    throw new ApiError(401, { detail: "Could not validate credentials." });
  }

  const doFetch = () => fetchWithTimeout(`${API_URL}${path}`, { ...options, headers, credentials: "include" });

  let response = await doFetch();
  if (response.status === 401 && !path.includes("/auth/refresh")) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await doFetch();
    } else if (Date.now() < _authDeadUntil && !isAuthEndpoint(path)) {
      // Refresh just failed hard — avoid parsing twice; surface 401.
    }
  }

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    throw new ApiError(response.status, payload);
  }
  return payload as T;
}

export async function registerUser(payload: {
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  role: UserRole;
  turnstile_token: string;
  device_fingerprint?: string;
  client_metadata?: Record<string, unknown>;
}) {
  return request<User>("/auth/register", { method: "POST", body: JSON.stringify(payload) });
}

export async function getUsers(params?: { include_inactive?: boolean; search?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.include_inactive) searchParams.set("include_inactive", "true");
  if (params?.search) searchParams.set("search", params.search);
  if (params?.limit !== undefined) searchParams.set("limit", String(params.limit));
  if (params?.offset !== undefined) searchParams.set("offset", String(params.offset));
  const query = searchParams.toString();
  return request<User[]>(`/users${query ? `?${query}` : ""}`);
}

export async function getUserCount(params?: { include_inactive?: boolean; search?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.include_inactive) searchParams.set("include_inactive", "true");
  if (params?.search) searchParams.set("search", params.search);
  const query = searchParams.toString();
  return request<{ count: number }>(`/users/count${query ? `?${query}` : ""}`);
}

export async function createUser(payload: {
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  role: UserRole;
}) {
  return request<User>("/users", { method: "POST", body: JSON.stringify(payload) });
}

export async function updateUser(userId: number, payload: Partial<{
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  role: UserRole;
  is_active: boolean;
}>) {
  return request<User>(`/users/${userId}`, { method: "PATCH", body: JSON.stringify(payload) });
}

export async function deleteUser(userId: number) {
  return request<{ detail: string }>(`/users/${userId}/permanent`, { method: "DELETE" });
}

export async function loginUser(payload: { email: string; password: string; turnstile_token: string; device_fingerprint?: string; client_metadata?: Record<string, unknown> }) {
  return request<{ access_token: string; refresh_token?: string; token_type: string }>("/auth/login", { method: "POST", body: JSON.stringify(payload) });
}

export async function logoutUser() {
  return request<{ detail: string }>("/auth/logout", { method: "POST" });
}

export async function forgotPassword(payload: { email: string; turnstile_token: string; device_fingerprint?: string; client_metadata?: Record<string, unknown> }) {
  return request<{ detail: string }>("/auth/forgot-password", { method: "POST", body: JSON.stringify(payload) });
}

export async function validateResetToken(token: string) {
  return request<{ valid: boolean }>(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
}

export async function resetPassword(payload: { token: string; new_password: string; turnstile_token: string; device_fingerprint?: string; client_metadata?: Record<string, unknown> }) {
  return request<{ detail: string }>("/auth/reset-password", { method: "POST", body: JSON.stringify(payload) });
}

async function mfaEnroll() {
  return request<{ secret: string; otpauth_uri: string; backup_codes: string[] }>("/auth/mfa/enroll", { method: "POST" });
}

async function mfaVerifySetup(payload: { code: string }) {
  return request<{ verified: boolean; mfa_enabled: boolean }>("/auth/mfa/verify-setup", { method: "POST", body: JSON.stringify(payload) });
}

async function mfaVerifyLogin(payload: { temp_token: string; code: string; device_fingerprint?: string }) {
  return request<{ access_token: string; refresh_token?: string; token_type: string }>("/auth/mfa/verify", { method: "POST", body: JSON.stringify(payload) });
}

async function mfaDisable() {
  return request<{ detail: string }>("/auth/mfa/disable", { method: "POST" });
}

async function mfaStatus() {
  return request<{ mfa_enabled: boolean; enrolled: boolean }>("/auth/mfa/status");
}

export async function inviteSubcontractor(
  payload: { email: string; full_name: string; company_name?: string; send_on?: string }
) {
  const { send_on, ...rest } = payload;
  const body = send_on ? { ...rest, send_on } : rest;
  return request<User>("/auth/invite-subcontractor", { method: "POST", body: JSON.stringify(body) });
}


export async function getMe() {
  return request<User>("/auth/me");
}

/** User ids this manager may edit (team + self). Admins get empty list = unrestricted. */
export async function getManagedTeamIds() {
  return request<{ user_ids: number[] }>("/auth/me/managed-team-ids");
}

export async function patchMe(payload: { dark_mode?: boolean; recent_compact?: boolean }) {
  return request<User>("/auth/me", { method: "PATCH", body: JSON.stringify(payload) });
}

export async function getJobs(includeInactive = false) {
  return request<Job[]>(`/jobs${includeInactive ? "?include_inactive=true" : ""}`);
}

export async function createJob(payload: { name: string; active?: boolean }) {
  return request<Job>("/jobs", { method: "POST", body: JSON.stringify(payload) });
}

export async function updateJob(jobId: number, payload: { name?: string; active?: boolean }) {
  return request<Job>(`/jobs/${jobId}`, { method: "PATCH", body: JSON.stringify(payload) });
}

export async function getCostCodes(search?: string) {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  return request<CostCode[]>(`/cost-codes${params}`);
}

export async function getCustomers(search?: string) {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  return request<Customer[]>(`/customers${params}`);
}

export async function syncCostCodes() {
  return request<{ status: string; created: number; updated: number; total: number }>("/sync/cost-codes", { method: "POST" });
}

export async function syncCustomers() {
  return request<{ status: string; created: number; updated: number; total: number }>("/sync/customers", { method: "POST" });
}

export async function syncTeams() {
  return request<{
    status: string;
    teams_upserted: number;
    managers_linked: number;
    users_updated: number;
    teams_fetched: number;
    assignments_fetched: number;
  }>("/sync/teams", { method: "POST" });
}

export async function syncTimeclockFromQB(lookbackDays?: number) {
  const params = lookbackDays !== undefined ? `?lookback_days=${lookbackDays}` : "";
  return request<{ status: string; created: number; updated: number; skipped: number }>(`/sync/timeclock-from-quickbase${params}`, { method: "POST" });
}

export async function syncFieldNotesFromQB(lookbackDays?: number) {
  const params = lookbackDays !== undefined ? `?lookback_days=${lookbackDays}` : "";
  return request<{
    status: string;
    fetched: number;
    created: number;
    updated: number;
    skipped: number;
    no_user?: number;
    errors?: number;
  }>(`/sync/field-notes-from-quickbase${params}`, { method: "POST" });
}

/** Push pending local field notes (no rid, or dirty ack) to the env-scoped QB table. */
export async function syncFieldNotesToQB() {
  return request<{ status: string; synced: number; failed: number }>("/sync/field-notes", {
    method: "POST",
  });
}

export async function retryQbPushes(limit = 40) {
  return request<{
    status: string;
    processed?: number;
    retried?: number;
    ok?: number;
    failed?: number;
    skipped?: number;
  }>(`/sync/qb-push-retry?limit=${encodeURIComponent(String(limit))}`, { method: "POST" });
}

export async function getIdentityIssues(limit = 50) {
  return request<{
    count: number;
    total_flagged: number;
    truncated: number;
    users: Array<{
      user_id: number;
      email?: string | null;
      full_name?: string | null;
      role?: string;
      qb_user_email?: string | null;
      qb_username?: string | null;
      qb_payroll_id?: string | null;
      qb_payroll_code?: string | null;
      co_code?: string | null;
      issues: string[];
      punches_90d: number;
    }>;
  }>(`/users/identity-issues?limit=${encodeURIComponent(String(limit))}`);
}

export async function linkUserIdentity(
  userId: number,
  payload: {
    qb_user_email?: string | null;
    qb_username?: string | null;
    qb_payroll_id?: string | null;
    qb_payroll_code?: string | null;
    co_code?: string | null;
    office_location?: string | null;
    qb_contact_id?: string | null;
  },
) {
  return request<User>(`/users/${userId}/link-identity`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getPayrollReconcile(params?: URLSearchParams) {
  const q = params?.toString() ? `?${params}` : "";
  return request<PayrollValidateResult>(`/entries/payroll-reconcile${q}`);
}

export type PayPeriodApproval = {
  id: number;
  user_id: number;
  period_from: string;
  period_to: string;
  pay_week?: string | null;
  employee_status: string;
  manager_status: string;
  employee_note?: string | null;
  manager_note?: string | null;
  employee_at?: string | null;
  manager_at?: string | null;
  email?: string | null;
  full_name?: string | null;
};

export async function getMyPayPeriodApprovals(year?: number) {
  const q = year ? `?year=${year}` : "";
  return request<{
    approvals: PayPeriodApproval[];
    current_period: {
      pay_week: string;
      from_date: string;
      to_date: string;
      label: string;
    } | null;
    current_approval: PayPeriodApproval | null;
  }>(`/pay-period-approvals/me${q}`);
}

export async function getMyApprovalForPeriod(periodFrom: string, periodTo: string) {
  const params = new URLSearchParams({ period_from: periodFrom, period_to: periodTo });
  return request<{ approval: PayPeriodApproval | null }>(
    `/pay-period-approvals/me/for-period?${params}`,
  );
}

export async function submitMyPayPeriodApproval(body: {
  period_from: string;
  period_to: string;
  pay_week?: string;
  employee_status: "approved" | "disputed";
  employee_note?: string;
}) {
  return request<{ approval: PayPeriodApproval }>("/pay-period-approvals/me", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listPayPeriodApprovals(statusFilter = "pending_manager") {
  return request<{ count: number; approvals: PayPeriodApproval[]; filter: string }>(
    `/pay-period-approvals?status_filter=${encodeURIComponent(statusFilter)}`,
  );
}

export async function managerReviewPayPeriod(
  approvalId: number,
  body: { manager_status: "approved" | "rejected"; manager_note?: string },
) {
  return request<{
    approval: PayPeriodApproval;
    email_sent?: boolean;
    email_error?: string | null;
  }>(`/pay-period-approvals/${approvalId}/manager-review`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function syncTimeclockToQB() {
  return request<{ status: string; processed: number; synced: number; failed: number }>("/sync/timeclock-to-quickbase", { method: "POST" });
}

async function submitTimeclock(payload: FormData) {
  return request<Entry>("/timeclock", { method: "POST", body: payload });
}

export type TimeclockFormFields = Record<string, string>;

export interface BuildTimeclockFormDataArgs {
  fields: TimeclockFormFields;
  clientUuid: string;
  selfieBlob?: Blob | null;
  selfieName?: string;
  syncDelayMs?: number;
  turnstileToken?: string | null;
  includeToken?: boolean;
}

/**
 * Audit-only "how long the punch sat on device" value for POST /timeclock.
 * Backend expects a non-negative integer string; float noise (e.g. 1249.0000000000073)
 * causes a permanent 422. Always send a whole number of milliseconds.
 */
export function toSyncDelayMsInt(delayMs: number | null | undefined): number {
  const n = Number(delayMs);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.max(0, Math.round(n));
}

/**
 * Builds the exact FormData shape expected by POST /timeclock.
 * Shared by the queued sync path and the direct (IndexedDB-fallback) path
 * so client_uuid, delay, selfie, and field fidelity stay identical.
 */
export function buildTimeclockFormData(args: BuildTimeclockFormDataArgs): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(args.fields)) {
    form.set(key, value);
  }
  form.set("client_uuid", args.clientUuid);
  form.set("sync_delay_ms", String(toSyncDelayMsInt(args.syncDelayMs)));
  if (args.includeToken !== false && args.turnstileToken) {
    form.set("cf-turnstile-response", args.turnstileToken);
  }
  if (args.selfieBlob) {
    form.set("selfie", args.selfieBlob, args.selfieName || "selfie.jpg");
  }
  return form;
}

/**
 * Punch submission for the offline-sync queue. Identical to submitTimeclock,
 * but surfaces whether the server deduplicated the punch (200 = a replay of a
 * punch that already landed, 201 = newly created).
 */
export async function submitTimeclockSync(payload: FormData): Promise<{ entry: Entry; deduplicated: boolean }> {
  const start = (typeof performance !== "undefined" && performance.now) ? performance.now() : 0;
  const response = await fetchWithTimeout(
    `${API_URL}/timeclock`,
    { method: "POST", body: payload, credentials: "include" },
    SYNC_SUBMIT_TIMEOUT_MS,
  );
  const dur = (typeof performance !== "undefined" && performance.now) ? Math.round(performance.now() - start) : 0;
  const contentType = response.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    throw new ApiError(response.status, data);
  }
  // Best-effort telemetry for image upload trace (only in browser main thread)
  if (typeof window !== "undefined" && dur > 0) {
    void import("@/lib/telemetry").then(({ startTrace, logEvent, getNetworkState, getDecision }) => {
      try {
        if (getDecision().perfEnabled) {
          const t = startTrace("image_upload");
          const kb = Math.round((payload as any).get ? (payload as any).get("selfie")?.size / 1024 || 0 : 0);
          t.stop({ upload_ms: dur, approx_kb: kb, network: getNetworkState().type });
        }
        if (getDecision().analyticsEnabled) {
          logEvent("image_upload_complete", { upload_ms: dur, network_type: getNetworkState().type });
        }
      } catch {}
    }).catch(() => {});
  }
  return { entry: data as Entry, deduplicated: response.status === 200 };
}

/**
 * Field note submission for the offline-sync queue (raw fetch so it can be used
 * from the DOM-free syncEngine / SW). Surfaces deduplication (200 = replay).
 * Payload is plain object (JSON body) + client_uuid + sync_delay_ms (added by caller).
 */
export async function submitFieldNoteSync(payload: Record<string, unknown>): Promise<{ note: FieldNote; deduplicated: boolean }> {
  const start = (typeof performance !== "undefined" && performance.now) ? performance.now() : 0;
  const body: Record<string, unknown> = { ...payload };
  if ("sync_delay_ms" in body) {
    body.sync_delay_ms = toSyncDelayMsInt(body.sync_delay_ms as number);
  }
  const response = await fetchWithTimeout(
    `${API_URL}/field-notes`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
    },
    SYNC_SUBMIT_TIMEOUT_MS,
  );
  const dur = (typeof performance !== "undefined" && performance.now) ? Math.round(performance.now() - start) : 0;
  const contentType = response.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    throw new ApiError(response.status, data);
  }
  if (typeof window !== "undefined" && dur > 0) {
    void import("@/lib/telemetry").then(({ logEvent, getDecision, getNetworkState }) => {
      try { if (getDecision().analyticsEnabled) logEvent("field_note_sync", { upload_ms: dur, network_type: getNetworkState().type }); } catch {}
    }).catch(() => {});
  }
  return { note: data as FieldNote, deduplicated: response.status === 200 };
}

export async function getEntries(params: URLSearchParams) {
  const query = params.toString();
  return request<Entry[]>(`/entries${query ? `?${query}` : ""}`);
}

export async function getEntryCount(params: URLSearchParams) {
  const query = params.toString();
  return request<{ count: number }>(`/entries/count${query ? `?${query}` : ""}`);
}

export type CorrectionCountRow = {
  user_id: number;
  name: string;
  count: number;
};

export type CorrectionFieldCount = {
  changed_field: string;
  count: number;
};

export type PunchCorrectionRead = {
  altered_at: string;
  clock_entry_id: number | null;
  changed_field: string;
  old_value: string;
  new_value: string;
  subject_user_id: number;
  subject_name: string;
  actor_user_id: number;
  actor_name: string;
};

export type PunchCorrectionStats = {
  total: number;
  by_subject: CorrectionCountRow[];
  by_actor: CorrectionCountRow[];
  by_field: CorrectionFieldCount[];
  recent: PunchCorrectionRead[];
};

export async function getCorrectionStats(params?: {
  from_date?: string;
  to_date?: string;
  limit?: number;
}) {
  const q = new URLSearchParams();
  if (params?.from_date) q.set("from_date", params.from_date);
  if (params?.to_date) q.set("to_date", params.to_date);
  if (params?.limit != null) q.set("limit", String(params.limit));
  const query = q.toString();
  return request<PunchCorrectionStats>(`/entries/correction-stats${query ? `?${query}` : ""}`);
}

export type EntryUpdatePayload = {
  field_notes?: string | null;
  recorded_time?: string | null;
  clock_out_time?: string | null;
  customer_id?: number | null;
  cost_code_id?: number | null;
};

/** Manager/admin: create a punch for another employee (requires ENABLE_MANAGER_PUNCH_CREATION). */
export async function createPunchOnBehalf(payload: OnBehalfPunchPayload): Promise<Entry> {
  return request<Entry>("/timeclock/on-behalf", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function updateEntry(entryId: number, payload: EntryUpdatePayload) {
  return request<Entry>(`/entries/${entryId}`, { method: "PATCH", body: JSON.stringify(payload) });
}

/** Admin-only: archive punch, delete from QuickBase when linked, remove live row. */
export async function deleteEntry(entryId: number): Promise<{
  detail: string;
  entry_id: number;
  archive_id: number;
  qb_record_id: string | null;
  qb_table_id: string | null;
  qb_deleted: boolean;
  qb_delete_status: string;
}> {
  return request(`/entries/${entryId}`, { method: "DELETE" });
}

/** Extract a refreshed Entry from a 409 conflict body (admin edit concurrency). */
export function entryFromConflictDetail(detail: unknown): Entry | null {
  if (!detail || typeof detail !== "object") return null;
  // FastAPI wraps HTTPException detail: { detail: { message, entry } }
  const outer = detail as { detail?: unknown; entry?: Entry; message?: string };
  const inner =
    outer.detail && typeof outer.detail === "object"
      ? (outer.detail as { entry?: Entry; message?: string })
      : outer;
  if (inner.entry && typeof inner.entry === "object" && typeof (inner.entry as Entry).id === "number") {
    return inner.entry as Entry;
  }
  return null;
}

/** Human message from a 409 admin-edit conflict body. */
export function messageFromConflictDetail(detail: unknown): string | null {
  if (!detail || typeof detail !== "object") return null;
  const outer = detail as { detail?: unknown; message?: string };
  const inner =
    outer.detail && typeof outer.detail === "object"
      ? (outer.detail as { message?: string })
      : outer;
  return typeof inner.message === "string" ? inner.message : null;
}

export async function exportEntries(params: URLSearchParams) {
  const response = await fetch(`${API_URL}/entries/export${params.toString() ? `?${params}` : ""}`, {
    credentials: "include",
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => response.text());
    throw new ApiError(response.status, payload);
  }
  return response.text();
}

/**
 * Admin-only (AZURE_ALLOWED_ADMIN_GROUP_ID) payroll batch export.
 * Supports ?format=csv|xlsx (xlsx only for payroll report for now).
 * Returns text for csv or Blob for xlsx.
 */
export async function exportPayrollReport(
  params: URLSearchParams,
  format: "csv" | "xlsx" = "csv",
  opts?: { requireApprovals?: boolean },
): Promise<string | Blob> {
  if (format === "xlsx") {
    params.set("format", "xlsx");
  }
  if (opts?.requireApprovals) {
    params.set("require_approvals", "true");
  }
  const response = await fetch(`${API_URL}/entries/payroll-export${params.toString() ? `?${params}` : ""}`, {
    credentials: "include",
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => response.text());
    throw new ApiError(response.status, payload);
  }
  if (format === "xlsx") {
    return response.blob();
  }
  return response.text();
}

/** Dry-run payroll batch: exclusions, missing Co/File, duration vs Reg+OT. */
export type PayrollValidateResult = {
  ok: boolean;
  ok_without_approvals?: boolean;
  from_date: string | null;
  to_date: string | null;
  pay_week: string | null;
  candidate_entries: number;
  included_entries: number;
  excluded_entries: number;
  exclusion_reason_counts: Record<string, number>;
  exclusions: Array<{
    entry_id?: number;
    email?: string | null;
    reason: string;
    duration?: number | null;
  }>;
  exclusions_truncated: number;
  payroll_rows: number;
  duration_hours_included: number;
  reg_hours: number;
  ot_hours: number;
  reg_plus_ot_hours: number;
  duration_vs_export_delta: number;
  file_mismatches: Array<{
    file_number: string | null;
    duration_hours: number;
    reg_plus_ot_hours: number;
    delta: number;
  }>;
  missing_co_code_rows: number;
  missing_file_rows: number;
  empty_export_with_candidates?: boolean;
  approvals?: {
    period_known?: boolean;
    period_from?: string;
    period_to?: string;
    employees_in_export: number;
    employee_approved: number;
    employee_disputed: number;
    employee_not_submitted: number;
    manager_approved: number;
    manager_pending: number;
    manager_rejected: number;
    ready_for_payroll: boolean;
    missing_employee_approval?: Array<{
      user_id?: number;
      email?: string | null;
      full_name?: string | null;
    }>;
    disputed?: Array<{
      email?: string | null;
      full_name?: string | null;
      employee_note?: string | null;
    }>;
  };
};

export async function validatePayrollExport(params: URLSearchParams): Promise<PayrollValidateResult> {
  return request<PayrollValidateResult>(
    `/entries/payroll-export/validate${params.toString() ? `?${params}` : ""}`,
  );
}

export async function getOpenPunches(minHours = 14) {
  return request<{
    min_hours: number;
    count: number;
    open_punches: Array<{
      entry_id: number;
      email?: string | null;
      full_name?: string | null;
      recorded_time?: string | null;
      age_hours?: number | null;
      site_name?: string;
      qb_record_id?: string | null;
      qb_push_status?: string | null;
      co_code?: string | null;
      qb_payroll_code?: string | null;
    }>;
  }>(`/entries/open-punches?min_hours=${encodeURIComponent(String(minHours))}`);
}

export async function getQbPushErrors(limit = 25) {
  return request<{
    count: number;
    errors: Array<{
      entry_id: number;
      email?: string | null;
      full_name?: string | null;
      qb_record_id?: string | null;
      qb_push_last_error?: string;
      qb_push_attempts?: number;
      qb_push_updated_at?: string | null;
      recorded_time?: string | null;
    }>;
  }>(`/entries/qb-push-errors?limit=${encodeURIComponent(String(limit))}`);
}

/** Fetch the list of pre-determined company pay weeks (for admin export dropdown).
 * Returns { pay_weeks: Array<{pay_week, from_date, to_date, label}> }
 */
async function getPayWeeks(year?: number) {
  const params = new URLSearchParams();
  if (year) params.set("year", String(year));
  const q = params.toString();
  return request<{ pay_weeks: Array<{ pay_week: string; from_date: string; to_date: string; label: string }> }>(
    `/entries/pay-weeks${q ? `?${q}` : ""}`
  );
}

/** New Employee Touch Detail export (per pay week). Same format/blob contract. */
export async function exportEmployeeTouchDetail(params: URLSearchParams, format: "csv" | "xlsx" = "csv"): Promise<string | Blob> {
  if (format === "xlsx") {
    params.set("format", "xlsx");
  }
  const response = await fetch(`${API_URL}/entries/employee-touch-detail${params.toString() ? `?${params}` : ""}`, {
    credentials: "include",
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => response.text());
    throw new ApiError(response.status, payload);
  }
  if (format === "xlsx") {
    return response.blob();
  }
  return response.text();
}

/** Accounting Touch Detail export (calendar month). Same format/blob contract. */
export async function exportAccountingTouchDetail(params: URLSearchParams, format: "csv" | "xlsx" = "csv"): Promise<string | Blob> {
  if (format === "xlsx") {
    params.set("format", "xlsx");
  }
  const response = await fetch(`${API_URL}/entries/accounting-touch-detail${params.toString() ? `?${params}` : ""}`, {
    credentials: "include",
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => response.text());
    throw new ApiError(response.status, payload);
  }
  if (format === "xlsx") {
    return response.blob();
  }
  return response.text();
}


export async function submitVendorSignup(payload: FormData) {
  return request<VendorSignupResponse>("/vendor-signup", { method: "POST", body: payload });
}

export async function getFieldNotes(params?: {
  processed?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
  all?: boolean;
  user_id?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params?.processed !== undefined) searchParams.set("processed", String(params.processed));
  if (params?.search) searchParams.set("search", params.search);
  if (params?.limit !== undefined) searchParams.set("limit", String(params.limit));
  if (params?.offset !== undefined) searchParams.set("offset", String(params.offset));
  if (params?.all !== undefined) searchParams.set("all", String(params.all));
  if (params?.user_id !== undefined) searchParams.set("user_id", String(params.user_id));
  const query = searchParams.toString();
  return request<FieldNote[]>(`/field-notes${query ? `?${query}` : ""}`);
}

export async function getFieldNoteCount(params?: { processed?: boolean; search?: string; all?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.processed !== undefined) searchParams.set("processed", String(params.processed));
  if (params?.search) searchParams.set("search", params.search);
  if (params?.all !== undefined) searchParams.set("all", String(params.all));
  const query = searchParams.toString();
  return request<{ count: number }>(`/field-notes/count${query ? `?${query}` : ""}`);
}

async function createFieldNote(payload: {
  additional_note?: string;
  correction_type: string;
  original_datetime?: string | null;
  corrected_datetime?: string | null;
  reason_for_correction: string;
}) {
  return request<FieldNote>("/field-notes", { method: "POST", body: JSON.stringify(payload) });
}

export async function processFieldNote(noteId: number) {
  return request<FieldNote>(`/field-notes/${noteId}/process`, { method: "PATCH", body: JSON.stringify({ processed: true }) });
}

export async function getPunchNotesReview(params?: { user_id?: number; limit?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.user_id !== undefined) searchParams.set("user_id", String(params.user_id));
  if (params?.limit !== undefined) searchParams.set("limit", String(params.limit));
  const query = searchParams.toString();
  return request<PunchNoteReviewRead[]>(`/entries/notes-review${query ? `?${query}` : ""}`);
}

export async function getPunchNotesReviewCount() {
  return request<{ count: number }>("/entries/notes-review/count");
}

export async function ackPunchNotesReview(entryId: number, field?: string) {
  return request<PunchNoteReviewRead>(`/entries/${entryId}/notes-review`, {
    method: "PATCH",
    body: JSON.stringify(field ? { field } : {}),
  });
}


export type MaintenanceBanner = {
  id: number;
  message: string;
  banner_starts_at: string | null;
  banner_ends_at: string | null;
  maintenance_starts_at: string | null;
  maintenance_ends_at: string | null;
  is_active: boolean;
  created_at: string;
};

export async function getMaintenanceBanner(): Promise<MaintenanceBanner | null> {
  const response = await fetch(`${API_URL}/maintenance-banner`, { credentials: "include" });
  if (response.status === 204) return null;
  if (!response.ok) return null;
  return response.json();
}

export async function getMaintenanceBannerForAdmin(): Promise<MaintenanceBanner | null> {
  const response = await fetch(`${API_URL}/maintenance-banner/manage`, { credentials: "include" });
  if (response.status === 204) return null;
  if (!response.ok) return null;
  return response.json();
}

export async function createMaintenanceBanner(payload: Omit<MaintenanceBanner, "id" | "created_at">): Promise<MaintenanceBanner> {
  return request<MaintenanceBanner>("/maintenance-banner", { method: "POST", body: JSON.stringify(payload) });
}

export async function updateMaintenanceBanner(id: number, payload: Partial<Omit<MaintenanceBanner, "id" | "created_at">>): Promise<MaintenanceBanner> {
  return request<MaintenanceBanner>(`/maintenance-banner/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
}

export async function deleteMaintenanceBanner(id: number): Promise<void> {
  return request<void>(`/maintenance-banner/${id}`, { method: "DELETE" });
}

let _lastReportTime = 0;
const REPORT_DEBOUNCE_MS = 5000;

// Known non-fatal / environmental noise — do not spam the server.
// With ?debug=true these still surface in the client debug panel (see surfaceClientErrorLocally).
const IGNORED_CLIENT_ERROR_PATTERNS = [
  /Failed to execute 'postMessage' on 'DOMWindow'.*challenges\.cloudflare\.com/i,
  /turnstile/i, // Turnstile internal errors are not actionable server-side
  /NetworkError when attempting to fetch resource/i,
  /Failed to fetch/i,
  /Load failed/i, // Safari network failure wording
  /Network request timed out/i,
  /The Internet connection appears to be offline/i,
  /^Performance:\s/i, // Firebase Performance attribute overflow noise
];

function shouldIgnoreClientError(message: string): boolean {
  return IGNORED_CLIENT_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * Always keep errors visible when the tech has debug mode on (?debug=true / localStorage).
 * Suppression only stops the *server* report — not the local debug buffer/console.
 */
function surfaceClientErrorLocally(message: string, stack?: string, suppressed = false): void {
  const prefix = suppressed ? "[client-error suppressed]" : "[client-error]";
  // Dynamic import avoids circular deps and keeps api.ts load light.
  void import("@/lib/client-logger")
    .then(({ isEnabled, clientLog }) => {
      if (isEnabled()) {
        clientLog("error", `${prefix} ${message}`, stack ? { stack: stack.slice(0, 1500) } : undefined);
      } else if (typeof console !== "undefined" && console.debug) {
        console.debug(prefix, message);
      }
    })
    .catch(() => {
      if (typeof console !== "undefined" && console.debug) {
        console.debug(prefix, message);
      }
    });
}

export async function reportClientError(error: Error | string, info?: { componentStack?: string }) {
  const message = typeof error === "string" ? error : error.message;
  const stack = typeof error === "string" ? undefined : error.stack;
  const componentStack = info?.componentStack;
  const combinedStack = stack || componentStack || undefined;

  if (shouldIgnoreClientError(message)) {
    // Skip backend POST, but still show in debug panel when ?debug=true.
    surfaceClientErrorLocally(message, combinedStack, true);
    return;
  }

  // Debounce and prevent loops if reporting itself fails
  const now = Date.now();
  if (now - _lastReportTime < REPORT_DEBOUNCE_MS) return;
  _lastReportTime = now;

  // Also mirror reportable errors into the debug buffer when enabled.
  surfaceClientErrorLocally(message, combinedStack, false);

  const payload = {
    message: message.slice(0, 500),
    stack: (combinedStack || "").slice(0, 2000),
    url: typeof window !== "undefined" ? window.location.href.slice(0, 500) : undefined,
    line: undefined as number | undefined,
    column: undefined as number | undefined,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : undefined,
  };

  try {
    await fetch(`${API_URL}/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    // Silently ignore: we don't want error reporting to break the app
  }
}

export function downloadText(filename: string, text: string, type = "text/plain;charset=utf-8") {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
